import { useEffect, useState } from "react";

const stats = [
  { value: "半决赛", label: "当前进行轮次", tone: "text-foreground-950" },
  { value: "2 席", label: "决赛席位待定", tone: "text-primary-600" },
  { value: "128 万", label: "累计参与投票", tone: "text-foreground-950" },
  { value: "S07", label: "本赛季代号", tone: "text-accent-600" },
];

const FINAL_DATE = new Date(2026, 8, 12, 20, 0, 0).getTime();

function useCountdown() {
  const calc = () => {
    const diff = Math.max(0, FINAL_DATE - Date.now());
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = window.setInterval(() => setT(calc()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return t;
}

function Unit({ v, l }: { v: number; l: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="min-w-[42px] rounded-lg bg-background-950/90 px-2 py-1.5 text-center font-label text-lg font-extrabold leading-none text-background-50 md:min-w-[52px] md:text-2xl">
        {String(v).padStart(2, "0")}
      </span>
      <span className="text-xs text-foreground-700 md:text-sm">{l}</span>
    </div>
  );
}

export default function Hero() {
  const t = useCountdown();

  return (
    <section id="top" className="relative overflow-hidden bg-background-50 pt-24 md:pt-28">
      {/* decorative top band */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary-500 via-secondary-400 to-accent-500"></div>

      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-16 pt-6 md:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:pb-24">
        {/* text column */}
        <div className="relative z-10">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-500 px-3 py-1 text-xs font-bold text-background-50">
              <span className="h-1.5 w-1.5 rounded-full bg-background-50 chg-pulse"></span>
              实时更新中
            </span>
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-accent-300 bg-accent-100 px-3 py-1 text-xs font-bold text-accent-700">
              半决赛 · 今晚 20:00
            </span>
          </div>

          <h1 className="mt-5 font-heading text-4xl leading-[1.15] tracking-tight text-foreground-950 md:text-6xl">
            吃瓜大赛<span className="text-primary-500">，</span>
            <br className="hidden lg:block" />
            赛况在手<span className="text-accent-600">不迷路</span>
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            赛程安排、晋级战报、实时动态速递一站掌握。看看当前进行到哪一轮、哪些选手成功晋级、
            下一场对决何时开打，追更吃瓜从不掉队。
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#schedule"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-calendar-check-line text-base" aria-hidden="true"></i>
              查看赛程安排
            </a>
            <a
              href="#report"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-newspaper-line text-base" aria-hidden="true"></i>
              读晋级战报
            </a>
          </div>

          {/* stats */}
          <dl className="mt-9 grid grid-cols-2 gap-x-4 gap-y-5 border-t border-background-200 pt-6 sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label}>
                <dt className="order-2 mt-1 text-xs text-foreground-600">{s.label}</dt>
                <dd className={`font-heading text-2xl md:text-[28px] ${s.tone}`}>{s.value}</dd>
              </div>
            ))}
          </dl>

          {/* countdown */}
          <div className="mt-7 flex flex-wrap items-center gap-4 rounded-2xl bg-background-100 p-4">
            <div className="flex items-center gap-2 pr-1">
              <i className="ri-time-line text-xl text-primary-600" aria-hidden="true"></i>
              <div>
                <p className="text-xs text-foreground-700">距 9·12 总决赛之夜</p>
                <p className="text-[10px] text-foreground-500">三局两胜 · 决出赛季瓜王</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Unit v={t.d} l="天" />
              <Unit v={t.h} l="时" />
              <Unit v={t.m} l="分" />
              <Unit v={t.s} l="秒" />
            </div>
          </div>
        </div>

        {/* image column */}
        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <div className="absolute -right-3 -top-3 hidden h-28 w-28 rounded-full bg-accent-200/70 md:block chg-bob"></div>
          <div className="relative overflow-hidden rounded-[28px] border-4 border-background-50 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)]">
            <img
              src="https://readdy.ai/api/search-image?query=cheerful%20cartoon%20watermelon%20characters%20with%20big%20smiles%20holding%20trophy%20and%20confetti%20streamers%2C%20vibrant%20coral%20red%20and%20fresh%20green%20palette%20on%20warm%20cream%20background%2C%20playful%20flat%20vector%20illustration%2C%20sporty%20competition%20energy%2C%20high%20saturation%2C%20clean%20composition&width=880&height=1060&seq=chigua-hero-2026&orientation=portrait"
              alt="吃瓜大赛 S07 赛季：俏皮的西瓜角色们举着奖杯庆祝晋级"
              className="h-[420px] w-full object-cover object-top md:h-[540px]"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background-950/60 to-transparent p-4 pt-12">
              <div className="flex items-center justify-between text-background-50">
                <div>
                  <p className="font-heading text-lg leading-none">S07 晋级名单已解锁</p>
                  <p className="mt-1 text-xs text-background-50/80">瓜小满 · 月半瓜 · 黑籽侦探 · 滚圆球球</p>
                </div>
                <a
                  href="#bracket"
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50 px-3.5 py-2 text-xs font-bold text-primary-600 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  查看晋级墙 <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="absolute -left-4 top-8 rounded-2xl bg-background-50 px-3.5 py-2.5 shadow-lg">
            <p className="text-[10px] text-foreground-500">本赛季</p>
            <p className="font-label text-lg font-extrabold text-primary-600">64 场对决</p>
          </div>
          <div className="absolute -bottom-4 right-2 flex items-center gap-2 rounded-2xl bg-background-50 px-3.5 py-2.5 shadow-lg">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-fire-line text-base" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-[10px] text-foreground-500">赛季热度</p>
              <p className="text-sm font-bold text-foreground-900">全网 8.6 亿浏览</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}